import { motion } from "framer-motion";
import { TrendingUp, ArrowUpRight, CheckCircle2, Zap, Activity } from "lucide-react";

export default function DashboardMock() {
  return (
    <div className="relative">
      {/* Glow */}
      <div className="absolute -inset-8 bg-gradient-to-tr from-blue-200/40 via-white to-slate-100/0 rounded-[2rem] blur-2xl" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative bg-white rounded-2xl border border-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] overflow-hidden"
        data-testid="hero-dashboard-mock"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-3 sm:px-5 py-2.5 sm:py-3 border-b border-slate-100 bg-slate-50/70">
          <div className="flex items-center gap-1.5">
            <span className="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-slate-200" />
            <span className="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-slate-200" />
            <span className="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-slate-200" />
          </div>
          <div className="text-[10px] sm:text-[11px] font-medium text-slate-500 tabular truncate px-2">
            <span className="hidden sm:inline">app.nodera.health · </span>Overview
          </div>
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-emerald-600 font-medium">
            <span className="relative inline-flex w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-emerald-500 pulse-dot" />
            </span>
            Live
          </div>
        </div>

        <div className="p-3 sm:p-5 grid grid-cols-2 sm:grid-cols-6 gap-3 sm:gap-4">
          {/* KPI row — 2-col grid on mobile (3rd card spans full width), 3-up via col-span-2 of 6 on desktop */}
          <KpiCard label="Monthly Recurring Revenue" value="$184,320" delta="+18.4%" />
          <KpiCard label="Active Programs" value="12" delta="+3" />
          <KpiCard
            label="Program Adoption"
            value="67%"
            delta="+9.2pt"
            mobileSpan="col-span-2"
          />

          {/* Chart card — full width on mobile, 4-of-6 on desktop */}
          <div className="col-span-2 sm:col-span-4 sm:row-span-2 rounded-xl border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="min-w-0">
                <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                  MRR by program
                </div>
                <div className="text-slate-900 font-display font-semibold text-sm sm:text-base">
                  Last 30 days
                </div>
              </div>
              <div className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium text-emerald-600 flex-shrink-0">
                <TrendingUp className="w-3 sm:w-3.5 h-3 sm:h-3.5" /> +22.8%
              </div>
            </div>
            <AreaChart />
            <div className="mt-2.5 sm:mt-3 flex items-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] text-slate-500 flex-wrap">
              <LegendDot color="bg-blue-600" label="Sleep" />
              <LegendDot color="bg-slate-900" label="Metabolic" />
              <LegendDot color="bg-blue-300" label="Hormone" />
              <LegendDot color="bg-slate-300" label="Diagnostics" />
            </div>
          </div>

          {/* Side workflow card — full width on mobile, 2-of-6 on desktop */}
          <div className="col-span-2 sm:col-span-2 sm:row-span-2 rounded-xl border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                Automation
              </div>
              <Zap className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-blue-600" />
            </div>
            <ul className="space-y-2 sm:space-y-2.5">
              {[
                { label: "Intake received", done: true },
                { label: "Vendor routed", done: true },
                { label: "Fulfillment scheduled", done: true },
                { label: "Patient notified", done: false },
                { label: "Invoice reconciled", done: false },
              ].map((s, i) => (
                <li key={i} className="flex items-center gap-2 text-[11px] sm:text-[12px]">
                  {s.done ? (
                    <CheckCircle2 className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <span className="w-3 sm:w-3.5 h-3 sm:h-3.5 rounded-full border border-slate-300 bg-white flex-shrink-0" />
                  )}
                  <span className={s.done ? "text-slate-700" : "text-slate-400"}>{s.label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 sm:mt-4 rounded-lg bg-slate-50 border border-slate-100 p-2 sm:p-2.5 flex items-center gap-2">
              <Activity className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-blue-600 flex-shrink-0" />
              <span className="text-[10px] sm:text-[11px] text-slate-600">94% tasks auto-routed</span>
            </div>
          </div>

          {/* Program list — full width at all breakpoints */}
          <div className="col-span-2 sm:col-span-6 rounded-xl border border-slate-200">
            <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-100">
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                Top programs
              </div>
              <div className="text-[10px] sm:text-[11px] text-slate-500">Auto-sorted by MRR</div>
            </div>
            <div className="divide-y divide-slate-100">
              {[
                { name: "Sleep Optimization", plan: "Subscription · Monthly", mrr: "$62,140", trend: "+12%", color: "bg-blue-600" },
                { name: "Metabolic Health", plan: "Subscription · Quarterly", mrr: "$48,900", trend: "+24%", color: "bg-slate-900" },
                { name: "Hormone & Longevity", plan: "Membership", mrr: "$41,280", trend: "+8%", color: "bg-blue-300" },
              ].map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5 gap-3">
                  <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full ${p.color} flex-shrink-0`} />
                    <div className="min-w-0">
                      <div className="text-[11px] sm:text-xs font-semibold text-slate-800 truncate">{p.name}</div>
                      <div className="text-[10px] sm:text-[10.5px] text-slate-400 truncate">{p.plan}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 tabular flex-shrink-0">
                    <div className="text-[11px] sm:text-xs font-semibold text-slate-900">{p.mrr}</div>
                    <div className="text-[10px] sm:text-[11px] font-medium text-emerald-600">{p.trend}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating KPI chip — desktop only (already hidden md and below) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="hidden md:flex absolute -right-6 top-10 bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2 items-center gap-2"
      >
        <div className="w-7 h-7 rounded-lg bg-blue-600/10 flex items-center justify-center">
          <ArrowUpRight className="w-3.5 h-3.5 text-blue-600" />
        </div>
        <div>
          <div className="text-[10px] text-slate-400 uppercase tracking-[0.18em] font-semibold">Retention</div>
          <div className="text-sm font-display font-semibold text-slate-900">91%</div>
        </div>
      </motion.div>
    </div>
  );
}

function KpiCard({ label, value, delta, mobileSpan = "" }) {
  return (
    <div className={`${mobileSpan} sm:col-span-2 rounded-xl border border-slate-200 p-3 sm:p-3.5`}>
      <div className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold leading-tight">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
        <div className="text-base sm:text-lg font-display font-bold text-slate-900 tabular">{value}</div>
        <div className="text-[10px] sm:text-[11px] font-medium text-emerald-600 tabular">{delta}</div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
      {label}
    </span>
  );
}

function AreaChart() {
  // Simple inline SVG area chart — preserveAspectRatio="none" lets it scale
  // crisply at all viewport widths.
  return (
    <svg viewBox="0 0 400 120" className="w-full h-20 sm:h-24" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="gradBlue" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="gradSlate" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#0f172a" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* grid */}
      {[20, 50, 80].map((y) => (
        <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="#f1f5f9" strokeDasharray="3 4" />
      ))}
      {/* slate series */}
      <path
        d="M0,80 C40,70 70,58 110,52 C150,46 180,58 220,50 C260,42 300,32 340,36 C370,40 390,34 400,30 L400,120 L0,120 Z"
        fill="url(#gradSlate)"
      />
      <path
        d="M0,80 C40,70 70,58 110,52 C150,46 180,58 220,50 C260,42 300,32 340,36 C370,40 390,34 400,30"
        fill="none"
        stroke="#0f172a"
        strokeWidth="1.6"
      />
      {/* blue series */}
      <path
        d="M0,95 C40,88 70,78 110,72 C150,66 180,74 220,64 C260,54 300,44 340,48 C370,50 390,42 400,36 L400,120 L0,120 Z"
        fill="url(#gradBlue)"
      />
      <path
        d="M0,95 C40,88 70,78 110,72 C150,66 180,74 220,64 C260,54 300,44 340,48 C370,50 390,42 400,36"
        fill="none"
        stroke="#2563eb"
        strokeWidth="1.8"
      />
    </svg>
  );
}
